// ============================================
// Bar Minigame — Cocktail Mixing & Crate Sorting
// Two mini-games for bar work shifts:
//   - Cocktail mixing (floor workers, outfit 2/3)
//   - Crate sorting (storage workers, outfit 1)
// Triggered via #ui:bar_cocktail / #ui:bar_crates
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { L } from '../systems/localization.js';

// ── UI tag values ──────────────────────────────────────────────────────
const UI_COCKTAIL = 'bar_cocktail';
const UI_CRATES   = 'bar_crates';

// ── Cocktail Recipes ───────────────────────────────────────────────────
const RECIPES = [
    // Easy (outfit 2) — 3 ingredients
    { name: 'Rum & Coke',   required: ['rum', 'cola', 'ice'],                        difficulty: 1 },
    { name: 'Gin & Tonic',  required: ['gin', 'tonic', 'lime'],                      difficulty: 1 },
    { name: 'Screwdriver',  required: ['vodka', 'orange_juice', 'ice'],               difficulty: 1 },
    // Hard (outfit 3) — 4 ingredients
    { name: 'Cosmopolitan', required: ['vodka', 'triple_sec', 'cranberry', 'lime'],   difficulty: 2 },
    { name: 'Mojito',       required: ['rum', 'lime', 'mint', 'soda'],                difficulty: 2 },
    { name: 'Margarita',    required: ['tequila', 'triple_sec', 'lime', 'salt'],      difficulty: 2 },
];

const ALL_INGREDIENTS = [
    { id: 'vodka',        label: 'Vodka',      icon: '\uD83C\uDF78', img: 'mg_bottle_vodka' },
    { id: 'rum',          label: 'Rum',         icon: '\uD83C\uDF7E', img: 'mg_bottle_rum' },
    { id: 'gin',          label: 'Gin',         icon: '\uD83C\uDF7B', img: 'mg_bottle_gin' },
    { id: 'tequila',      label: 'Tequila',     icon: '\uD83C\uDF75', img: 'mg_bottle_tequila' },
    { id: 'triple_sec',   label: 'Triple Sec',  icon: '\uD83C\uDF4A', img: 'mg_bottle_triplesec' },
    { id: 'cola',         label: 'Cola',         icon: '\u2615',      img: 'mg_glass_cola' },
    { id: 'tonic',        label: 'Tonic',        icon: '\uD83E\uDDCA', img: 'mg_glass_tonic' },
    { id: 'orange_juice', label: 'OJ',           icon: '\uD83E\uDDC3', img: 'mg_glass_oj' },
    { id: 'cranberry',    label: 'Cranberry',    icon: '\uD83C\uDF53', img: 'mg_glass_cranberry' },
    { id: 'soda',         label: 'Soda',         icon: '\uD83E\uDDCB', img: 'mg_glass_soda' },
    { id: 'lime',         label: 'Lime',         icon: '\uD83C\uDF4B', img: 'mg_fruit_lime' },
    { id: 'mint',         label: 'Mint',         icon: '\uD83C\uDF3F', img: 'mg_herb_mint' },
    { id: 'ice',          label: 'Ice',          icon: '\u2744\uFE0F', img: 'mg_item_ice' },
    { id: 'salt',         label: 'Salt',         icon: '\uD83E\uDDC2', img: 'mg_item_salt' },
];

// ── Background images ──────────────────────────────────────────────────
const BG_COCKTAIL = 'assets/images/Minigame/mg_bg_cocktail.png';
const BG_CRATES   = 'assets/images/Minigame/mg_bg_crates.png';
const IMG_BASE    = 'assets/images/Minigame/';

// ── Crate Data ─────────────────────────────────────────────────────────
const CRATE_ZONES = ['Beer', 'Wine', 'Spirits', 'Mixers'];

const CRATE_ITEMS = [
    { id: 'lager_case',     labelFb: 'Lager Case',      category: 'Beer',    icon: '\uD83C\uDF7A' },
    { id: 'ipa_box',        labelFb: 'IPA Box',         category: 'Beer',    icon: '\uD83C\uDF7A' },
    { id: 'stout_case',     labelFb: 'Stout Case',      category: 'Beer',    icon: '\uD83C\uDF7A' },
    { id: 'red_wine_case',  labelFb: 'Red Wine Case',   category: 'Wine',    icon: '\uD83C\uDF77' },
    { id: 'white_wine_box', labelFb: 'White Wine Box',  category: 'Wine',    icon: '\uD83C\uDF77' },
    { id: 'champagne_box',  labelFb: 'Champagne Box',   category: 'Wine',    icon: '\uD83C\uDF7E' },
    { id: 'whiskey_crate',  labelFb: 'Whiskey Crate',   category: 'Spirits', icon: '\uD83E\uDD43' },
    { id: 'vodka_crate',    labelFb: 'Vodka Crate',     category: 'Spirits', icon: '\uD83C\uDF78' },
    { id: 'tequila_box',    labelFb: 'Tequila Box',     category: 'Spirits', icon: '\uD83C\uDF75' },
    { id: 'tonic_cases',    labelFb: 'Tonic Cases',     category: 'Mixers',  icon: '\uD83E\uDDCB' },
    { id: 'soda_crate',     labelFb: 'Soda Crate',      category: 'Mixers',  icon: '\uD83E\uDDCB' },
    { id: 'juice_box',      labelFb: 'Juice Box',       category: 'Mixers',  icon: '\uD83E\uDDC3' },
];
// Crate label getter (L.get with fallback to EN id-based key)
const _crateLabel = (c) => L.get(`bar_mg.crate.${c.id}.label`, c.labelFb);

const ZONE_ICONS = { Beer: '\uD83C\uDF7A', Wine: '\uD83C\uDF77', Spirits: '\uD83E\uDD43', Mixers: '\uD83E\uDDCB' };

// Per-category crate art (Vertex AI Imagen 4.0 — see tools/gen_crate_variants.py)
const CRATE_IMG = {
    Beer:    'mg_crate_beer.png',
    Wine:    'mg_crate_wine.png',
    Spirits: 'mg_crate_spirits.png',
    Mixers:  'mg_crate_mixers.png',
};

// ── Performance Tiers ──────────────────────────────────────────────────
const COCKTAIL_TIERS = [
    { min: 0, label: 'Spilled It',   tier: 0, bonus: 0,  stars: 0 },
    { min: 3, label: 'Passable',     tier: 1, bonus: 10, stars: 2 },
    { min: 6, label: 'Well Mixed',   tier: 2, bonus: 20, stars: 4 },
    { min: 9, label: 'Perfect Pour', tier: 3, bonus: 30, stars: 5 },
];

const CRATE_TIERS = [
    { min: 0, label: 'Messy',        tier: 0, bonus: 0,  stars: 0 },
    { min: 3, label: 'Got Most',     tier: 1, bonus: 10, stars: 2 },
    { min: 5, label: 'Well Sorted',  tier: 2, bonus: 20, stars: 4 },
    { min: 7, label: 'Speed Demon',  tier: 3, bonus: 30, stars: 5 },
];

// ── Timers ─────────────────────────────────────────────────────────────
const TIMER_COCKTAIL = 15000;
const TIMER_CRATES   = 20000;
const GRID_SIZE      = 8;
const CRATE_COUNT    = 7;

// ============================================================================
// BarMinigame Class
// ============================================================================

class BarMinigame {
    constructor() {
        this._root = null;
        this._isActive = false;
        this._mode = null; // 'cocktail' or 'crates'

        // Cocktail state
        this._recipe = null;
        this._gridItems = [];
        this._selected = new Set();
        this._score = 0;
        this._wrongPicks = 0;

        // Crate state
        this._crates = [];
        this._activeCrate = null;
        this._sortedCorrect = 0;
        this._sortedTotal = 0;

        // Timer
        this._timerStart = 0;
        this._timerDuration = 0;
        this._timerInterval = null;
        this._finished = false;

        this._scope = events.createScope('bar-minigame');
        this._injectStyles();
        this._bindEvents();
    }

    isActive() { return this._isActive; }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        this._stopTimer();
    }

    // ── Events ──────────────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.TAG_UI, (mode) => {
            if (mode === UI_COCKTAIL) {
                this._activate('cocktail');
            } else if (mode === UI_CRATES) {
                this._activate('crates');
            } else if (this._isActive) {
                this._deactivate(false);
            }
        });
    }

    // ── Activation / Deactivation ───────────────────────────────────────

    _activate(mode) {
        this._stopTimer();
        this._mode = mode;
        this._score = 0;
        this._wrongPicks = 0;
        this._selected = new Set();
        this._activeCrate = null;
        this._sortedCorrect = 0;
        this._sortedTotal = 0;
        this._finished = false;
        this._isActive = true;

        storyManager.waitForInput();

        if (!this._root) this._createContainer();

        // Hide #story while the minigame owns the center. Without this, a
        // compact panel leaves the prior scene image + text visible below —
        // confusing (showering in an apartment while "mixing a Screwdriver").
        const story = document.getElementById('story');
        if (story) {
            this._storyPrevDisplay = story.style.display || '';
            story.style.display = 'none';
        }

        if (mode === 'cocktail') {
            this._setupCocktail();
        } else {
            this._setupCrates();
        }
        this._root.classList.remove('hidden');
    }

    _deactivate(resume = true) {
        this._isActive = false;
        this._stopTimer();
        if (this._root) this._root.classList.add('hidden');
        const story = document.getElementById('story');
        if (story) story.style.display = this._storyPrevDisplay || '';
        if (resume) storyManager.releaseInput();
    }

    // ── DOM ─────────────────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'bar-minigame-panel';
        el.className = 'bar-mg-panel hidden';
        const story = document.getElementById('story');
        if (story) {
            story.parentNode.insertBefore(el, story);
        } else {
            document.body.appendChild(el);
        }
        this._root = el;
    }

    // ====================================================================
    // COCKTAIL MODE
    // ====================================================================

    _setupCocktail() {
        // Pick recipe based on outfit tier
        let outfit = 2;
        try {
            const story = storyManager.currentStory;
            if (story) outfit = story.variablesState['bar_outfit'] || 2;
        } catch(e) { /* default */ }

        const difficulty = outfit >= 3 ? 2 : 1;
        const pool = RECIPES.filter(r => r.difficulty === difficulty);
        this._recipe = pool[Math.floor(Math.random() * pool.length)];

        // Build grid: required ingredients + random distractors
        const required = new Set(this._recipe.required);
        const distractors = ALL_INGREDIENTS
            .filter(i => !required.has(i.id))
            .sort(() => Math.random() - 0.5)
            .slice(0, GRID_SIZE - required.size);

        this._gridItems = [
            ...ALL_INGREDIENTS.filter(i => required.has(i.id)),
            ...distractors,
        ].sort(() => Math.random() - 0.5);

        this._buildCocktailUI();
        this._startTimer(TIMER_COCKTAIL);
    }

    _buildCocktailUI() {
        const recipe = this._recipe;
        this._root.innerHTML = '';

        // Header: recipe order
        const header = document.createElement('div');
        header.className = 'bar-mg-header';
        header.innerHTML = `
            <div class="bar-mg-title"><img class="bar-mg-title-icon" src="assets/images/UI/icon_cocktail_mix.png" alt="" aria-hidden="true">${recipe.name}</div>
            <div class="bar-mg-recipe">${recipe.required.map(id => {
                const ing = ALL_INGREDIENTS.find(i => i.id === id);
                return ing ? ing.label : id;
            }).join(' + ')}</div>
        `;
        this._root.appendChild(header);

        // Timer bar (sticky — stays visible while panel scrolls)
        const timerBar = document.createElement('div');
        timerBar.className = 'bar-mg-timer-track';
        timerBar.innerHTML = '<div class="bar-mg-timer-fill" id="bar-mg-timer"></div><span class="bar-mg-timer-text" id="bar-mg-timer-text"></span>';
        this._root.appendChild(timerBar);

        // Feedback area
        const feedback = document.createElement('div');
        feedback.className = 'bar-mg-feedback';
        feedback.id = 'bar-mg-feedback';
        feedback.textContent = L.get('bar_mg.cocktail.intro', 'Pick the right ingredients!');
        this._root.appendChild(feedback);

        // Selected display
        const selectedArea = document.createElement('div');
        selectedArea.className = 'bar-mg-selected';
        selectedArea.id = 'bar-mg-selected';
        this._root.appendChild(selectedArea);

        // Ingredient grid
        const grid = document.createElement('div');
        grid.className = 'bar-mg-grid';
        for (const item of this._gridItems) {
            const btn = document.createElement('button');
            btn.className = 'bar-mg-btn';
            btn.dataset.id = item.id;
            const imgSrc = item.img ? `${IMG_BASE}${item.img}.png` : '';
            btn.innerHTML = imgSrc
                ? `<img class="bar-mg-img" src="${imgSrc}" alt="${item.label}"><span class="bar-mg-label">${item.label}</span>`
                : `<span class="bar-mg-icon">${item.icon}</span><span class="bar-mg-label">${item.label}</span>`;
            btn.addEventListener('click', () => this._onIngredientClick(item));
            grid.appendChild(btn);
        }
        this._root.appendChild(grid);

        // Set background
        this._root.style.backgroundImage = `linear-gradient(rgba(14, 11, 20, 0.88), rgba(14, 11, 20, 0.92)), url('${BG_COCKTAIL}')`;
        this._root.style.backgroundSize = 'cover';
        this._root.style.backgroundPosition = 'center';

        // Stars
        const stars = document.createElement('div');
        stars.className = 'bar-mg-stars';
        stars.id = 'bar-mg-stars';
        stars.innerHTML = this._renderStars(0);
        this._root.appendChild(stars);

        // Skip button
        const skip = document.createElement('button');
        skip.className = 'bar-mg-skip';
        skip.textContent = 'Skip';
        skip.addEventListener('click', () => this._onSkip());
        this._root.appendChild(skip);
    }

    _onIngredientClick(item) {
        if (this._finished || this._selected.has(item.id)) return;

        const isCorrect = this._recipe.required.includes(item.id);
        this._selected.add(item.id);

        const btn = this._root.querySelector(`[data-id="${item.id}"]`);
        if (btn) {
            btn.disabled = true;
            if (isCorrect) {
                btn.classList.add('bar-mg-correct');
                this._score += 2;
            } else {
                btn.classList.add('bar-mg-wrong');
                this._score -= 1;
                this._wrongPicks++;
                // Shake
                btn.classList.add('bar-mg-shake');
                setTimeout(() => btn.classList.remove('bar-mg-shake'), 400);
            }
        }

        // Update feedback
        const fb = document.getElementById('bar-mg-feedback');
        if (fb) {
            if (isCorrect) {
                fb.textContent = 'Good pick!';
                fb.className = 'bar-mg-feedback bar-mg-fb-good';
            } else {
                fb.textContent = 'Wrong ingredient...';
                fb.className = 'bar-mg-feedback bar-mg-fb-bad';
            }
        }

        // Update selected display
        this._updateSelectedDisplay();
        this._updateStars('cocktail');

        // Check completion: all required found?
        const found = this._recipe.required.filter(id => this._selected.has(id));
        if (found.length === this._recipe.required.length) {
            this._finishCocktail();
        }
    }

    _updateSelectedDisplay() {
        const el = document.getElementById('bar-mg-selected');
        if (!el) return;
        const correct = this._recipe.required.filter(id => this._selected.has(id));
        el.innerHTML = correct.map(id => {
            const ing = ALL_INGREDIENTS.find(i => i.id === id);
            return `<span class="bar-mg-sel-item">${ing ? ing.icon : ''} ${ing ? ing.label : id}</span>`;
        }).join('');
    }

    _finishCocktail() {
        if (this._finished) return;
        this._finished = true;

        // Time bonus
        const elapsed = Date.now() - this._timerStart;
        const pct = 1 - (elapsed / this._timerDuration);
        if (pct > 0.5) this._score += 3;
        else if (pct > 0.25) this._score += 1;

        this._applyResults(COCKTAIL_TIERS);
    }

    // ====================================================================
    // CRATE MODE
    // ====================================================================

    _setupCrates() {
        // Pick random crates
        const shuffled = [...CRATE_ITEMS].sort(() => Math.random() - 0.5);
        this._crates = shuffled.slice(0, CRATE_COUNT).map((c, i) => ({ ...c, idx: i, sorted: false }));
        this._activeCrate = null;
        this._sortedCorrect = 0;
        this._sortedTotal = 0;

        this._buildCratesUI();
        this._startTimer(TIMER_CRATES);
    }

    _buildCratesUI() {
        this._root.innerHTML = '';

        // Header
        const header = document.createElement('div');
        header.className = 'bar-mg-header';
        header.innerHTML = `
            <div class="bar-mg-title"><img class="bar-mg-title-icon" src="assets/images/UI/icon_crate_sort.png" alt="" aria-hidden="true">Sort the Delivery</div>
            <div class="bar-mg-recipe">Tap a crate, then tap the right zone</div>
        `;
        this._root.appendChild(header);

        // Timer bar (sticky — stays visible while panel scrolls)
        const timerBar = document.createElement('div');
        timerBar.className = 'bar-mg-timer-track';
        timerBar.innerHTML = '<div class="bar-mg-timer-fill" id="bar-mg-timer"></div><span class="bar-mg-timer-text" id="bar-mg-timer-text"></span>';
        this._root.appendChild(timerBar);

        // Storage zones
        const zones = document.createElement('div');
        zones.className = 'bar-mg-zones';
        for (const zone of CRATE_ZONES) {
            const zoneEl = document.createElement('button');
            zoneEl.className = 'bar-mg-zone';
            zoneEl.dataset.zone = zone;
            zoneEl.innerHTML = `<span class="bar-mg-zone-icon">${ZONE_ICONS[zone]}</span><span class="bar-mg-zone-label">${zone}</span><span class="bar-mg-zone-count" id="zone-count-${zone}">0</span>`;
            zoneEl.addEventListener('click', () => this._onZoneTap(zone));
            zones.appendChild(zoneEl);
        }
        this._root.appendChild(zones);

        // Feedback
        const feedback = document.createElement('div');
        feedback.className = 'bar-mg-feedback';
        feedback.id = 'bar-mg-feedback';
        feedback.textContent = L.get('bar_mg.crate.intro', 'Tap a crate to pick it up');
        this._root.appendChild(feedback);

        // Crate queue
        const queue = document.createElement('div');
        queue.className = 'bar-mg-crate-queue';
        queue.id = 'bar-mg-crates';
        for (const crate of this._crates) {
            const el = document.createElement('button');
            el.className = 'bar-mg-crate';
            el.dataset.idx = crate.idx;
            const imgFile = CRATE_IMG[crate.category] || 'mg_crate_wood.png';
            el.innerHTML = `<img class="bar-mg-crate-img" src="${IMG_BASE}${imgFile}" alt="crate"><span class="bar-mg-crate-label">${_crateLabel(crate)}</span>`;
            el.addEventListener('click', () => this._onCrateTap(crate));
            queue.appendChild(el);
        }
        this._root.appendChild(queue);

        // Set background
        this._root.style.backgroundImage = `linear-gradient(rgba(14, 11, 20, 0.85), rgba(14, 11, 20, 0.90)), url('${BG_CRATES}')`;
        this._root.style.backgroundSize = 'cover';
        this._root.style.backgroundPosition = 'center';

        // Stars
        const stars = document.createElement('div');
        stars.className = 'bar-mg-stars';
        stars.id = 'bar-mg-stars';
        stars.innerHTML = this._renderStars(0);
        this._root.appendChild(stars);

        // Skip
        const skip = document.createElement('button');
        skip.className = 'bar-mg-skip';
        skip.textContent = 'Skip';
        skip.addEventListener('click', () => this._onSkip());
        this._root.appendChild(skip);
    }

    _onCrateTap(crate) {
        if (this._finished || crate.sorted) return;

        // Deselect previous
        this._root.querySelectorAll('.bar-mg-crate.bar-mg-crate-active').forEach(el => {
            el.classList.remove('bar-mg-crate-active');
        });

        this._activeCrate = crate;
        const el = this._root.querySelector(`[data-idx="${crate.idx}"]`);
        if (el) el.classList.add('bar-mg-crate-active');

        const fb = document.getElementById('bar-mg-feedback');
        if (fb) {
            fb.textContent = L.get('bar_mg.tap_zone', '{0} - tap a zone').replace('{0}', _crateLabel(crate));
            fb.className = 'bar-mg-feedback';
        }
    }

    _onZoneTap(zone) {
        if (this._finished || !this._activeCrate) return;

        const crate = this._activeCrate;
        const isCorrect = crate.category === zone;
        crate.sorted = true;
        this._sortedTotal++;
        this._activeCrate = null;

        const crateEl = this._root.querySelector(`[data-idx="${crate.idx}"]`);
        const fb = document.getElementById('bar-mg-feedback');

        if (isCorrect) {
            this._sortedCorrect++;
            if (crateEl) {
                crateEl.classList.add('bar-mg-crate-done');
                crateEl.disabled = true;
            }
            if (fb) {
                fb.textContent = L.get('bar_mg.feedback.correct', 'Correct!');
                fb.className = 'bar-mg-feedback bar-mg-fb-good';
            }
            // Update zone count
            const countEl = document.getElementById(`zone-count-${zone}`);
            if (countEl) countEl.textContent = parseInt(countEl.textContent) + 1;
        } else {
            if (crateEl) {
                crateEl.classList.add('bar-mg-shake', 'bar-mg-crate-wrong');
                setTimeout(() => {
                    crateEl.classList.remove('bar-mg-shake', 'bar-mg-crate-wrong', 'bar-mg-crate-active');
                    crate.sorted = false; // Allow retry
                }, 500);
            }
            if (fb) {
                fb.textContent = L.get('bar_mg.feedback.wrong', "Wrong! {0} doesn't go in {1}")
                    .replace('{0}', _crateLabel(crate))
                    .replace('{1}', zone);
                fb.className = 'bar-mg-feedback bar-mg-fb-bad';
            }
            this._sortedTotal--; // Don't count wrong attempts
        }

        this._updateStars('crates');

        // Check if all sorted
        if (this._crates.every(c => c.sorted)) {
            this._finishCrates();
        }
    }

    _finishCrates() {
        if (this._finished) return;
        this._finished = true;

        this._score = this._sortedCorrect;
        // Time bonus
        const elapsed = Date.now() - this._timerStart;
        const pct = 1 - (elapsed / this._timerDuration);
        if (pct > 0.5 && this._sortedCorrect === this._crates.length) this._score += 2;

        this._applyResults(CRATE_TIERS);
    }

    // ====================================================================
    // SHARED
    // ====================================================================

    _onSkip() {
        if (this._finished) return;
        this._finished = true;
        this._writeResults(0);
        setTimeout(() => this._deactivate(true), 300);
    }

    _applyResults(tiers) {
        this._stopTimer();
        const tier = this._getTier(tiers);

        // Show result
        const fb = document.getElementById('bar-mg-feedback');
        if (fb) {
            if (tier.bonus > 0) {
                fb.textContent = `${tier.label}! +$${tier.bonus} tip`;
                fb.className = 'bar-mg-feedback bar-mg-fb-good';
            } else {
                fb.textContent = tier.label;
                fb.className = 'bar-mg-feedback bar-mg-fb-bad';
            }
        }

        // Disable all buttons
        this._root.querySelectorAll('button:not(.bar-mg-skip)').forEach(b => {
            b.disabled = true;
            b.classList.add('bar-mg-done');
        });

        this._updateStars(this._mode === 'cocktail' ? 'cocktail' : 'crates');
        this._writeResults(tier.bonus);

        setTimeout(() => this._deactivate(true), 3000);
    }

    _writeResults(bonus) {
        try {
            const story = storyManager.currentStory;
            if (story) {
                story.variablesState['bar_minigame_bonus'] = bonus;
            }
        } catch(e) {
            console.warn('[BarMinigame] Could not write results:', e);
        }
    }

    _getTier(tiers) {
        for (let i = tiers.length - 1; i >= 0; i--) {
            if (this._score >= tiers[i].min) return tiers[i];
        }
        return tiers[0];
    }

    // ── Timer ───────────────────────────────────────────────────────────

    _startTimer(ms) {
        this._timerStart = Date.now();
        this._timerDuration = ms;
        // Cache DOM refs once instead of getElementById 20x/s. If the UI hasn't
        // been built yet (race during _activate), the lookups return null and
        // the tick is a no-op until the next _startTimer call.
        const bar = document.getElementById('bar-mg-timer');
        const txt = document.getElementById('bar-mg-timer-text');
        this._timerInterval = setInterval(() => {
            const elapsed = Date.now() - this._timerStart;
            const pct = Math.max(0, 1 - elapsed / ms);
            if (bar) bar.style.width = `${pct * 100}%`;
            if (txt) {
                const left = Math.max(0, Math.ceil((ms - elapsed) / 1000));
                txt.textContent = `${left}s`;
            }

            if (elapsed >= ms) {
                this._onTimerExpired();
            }
        }, 50);
    }

    _stopTimer() {
        if (this._timerInterval) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }
    }

    _onTimerExpired() {
        if (this._finished) return;
        this._finished = true;
        this._stopTimer();

        if (this._mode === 'cocktail') {
            // Score whatever was picked so far
            this._applyResults(COCKTAIL_TIERS);
        } else {
            this._score = this._sortedCorrect;
            this._applyResults(CRATE_TIERS);
        }
    }

    // ── Stars ───────────────────────────────────────────────────────────

    _renderStars(count) {
        let html = '';
        for (let i = 0; i < 5; i++) {
            html += `<span class="bar-mg-star ${i < count ? 'bar-mg-star-filled' : 'bar-mg-star-empty'}">\u2605</span>`;
        }
        return html;
    }

    _updateStars(mode) {
        const tiers = mode === 'cocktail' ? COCKTAIL_TIERS : CRATE_TIERS;
        const tempScore = mode === 'crates' ? this._sortedCorrect : this._score;
        let stars = 0;
        for (let i = tiers.length - 1; i >= 0; i--) {
            if (tempScore >= tiers[i].min) { stars = tiers[i].stars; break; }
        }
        const el = document.getElementById('bar-mg-stars');
        if (el) el.innerHTML = this._renderStars(stars);
    }

    // ── CSS ─────────────────────────────────────────────────────────────

    _injectStyles() {
        if (document.getElementById('bar-minigame-styles')) return;

        const style = document.createElement('style');
        style.id = 'bar-minigame-styles';
        style.textContent = `
/* ============================================
   Bar Minigame Panel
   ============================================ */

.bar-mg-panel {
    background: rgba(14, 11, 20, 0.95);
    border: 1px solid rgba(200, 160, 80, 0.15);
    border-radius: 12px;
    padding: 12px 14px 14px;
    margin: 8px auto;
    max-width: 640px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(200, 160, 80, 0.05);
    /* Panel sits above #story in .center-panel (parent has overflow:hidden).
       It must cap at the parent height and scroll internally — otherwise a tall
       grid (8 ingredients, 2-col on mobile) or elevated UI scale pushes the
       bottom ingredient row + Skip button below the overflow:hidden clip with
       no way to reach them. foundby:CutieLvr (F95) "can't scroll to see all the
       ingredients". min-height:0 lets the flex item shrink below content so the
       scroll engages; the sticky timer (top:0) stays pinned while scrolling. */
    flex: 0 1 auto;
    min-height: 0;
    max-height: calc(100% - 16px);
    overflow-y: auto;
    overscroll-behavior: contain;
    position: relative;
}

.bar-mg-panel.hidden { display: none; }

/* ── Header ─────────────────────────────── */

.bar-mg-header {
    text-align: center;
    padding: 6px 12px;
    border-radius: 8px;
    background: rgba(12, 8, 20, 0.88);
}

.bar-mg-title {
    font-size: 15px;
    font-weight: 600;
    color: #e8c860;
    margin-bottom: 2px;
}

.bar-mg-title-icon {
    width: 22px;
    height: 22px;
    object-fit: contain;
    vertical-align: -5px;
    margin-right: 6px;
}

.bar-mg-recipe {
    font-size: 13px;
    color: var(--text-body, #c8beda);
    font-style: italic;
}

/* ── Timer (sticky) ──────────────────────── */

.bar-mg-timer-track {
    position: sticky;
    top: 0;
    z-index: 3;
    height: 18px;
    background: rgba(20, 14, 30, 0.92);
    border: 1px solid rgba(200, 160, 80, 0.2);
    border-radius: 9px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.bar-mg-timer-fill {
    height: 100%;
    width: 100%;
    background: linear-gradient(90deg, #c8a050, #e8c860);
    border-radius: 8px 0 0 8px;
    transition: width 0.1s linear;
}

.bar-mg-timer-text {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: #120a1c;
    letter-spacing: 0.5px;
    text-shadow: 0 0 4px rgba(255, 230, 160, 0.6);
    pointer-events: none;
}

/* ── Feedback ────────────────────────────── */

.bar-mg-feedback {
    min-height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    font-style: italic;
    color: var(--text-body, #c8beda);
    text-align: center;
    padding: 4px 12px;
    border-radius: 8px;
    background: rgba(12, 8, 20, 0.88);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
    transition: color 0.2s ease;
}

.bar-mg-fb-good { color: #f0d060; text-shadow: 0 0 8px rgba(232, 200, 96, 0.4), 0 1px 4px rgba(0, 0, 0, 0.8); }
.bar-mg-fb-bad  { color: var(--text-faint, #706480); }

/* ── Selected ingredients display ────────── */

.bar-mg-selected {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: center;
    min-height: 28px;
    padding: 6px 14px;
    border-radius: 8px;
    background: rgba(12, 8, 20, 0.88);
}

.bar-mg-sel-item {
    font-size: 13px;
    font-weight: 500;
    color: #f0d060;
    padding: 2px 0;
}

/* ── Ingredient Grid (Cocktail) ──────────── */

.bar-mg-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.bar-mg-btn {
    min-height: 42px;
    padding: 6px 4px;
    background: linear-gradient(145deg, rgba(28, 22, 48, 0.9), rgba(20, 16, 36, 0.95));
    border: 1px solid rgba(60, 48, 80, 0.6);
    border-radius: 8px;
    color: var(--text, #ddd4ec);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    transition: all 0.2s ease;
}

.bar-mg-btn:hover:not(:disabled) {
    background: rgba(36, 28, 60, 0.95);
    border-color: rgba(200, 160, 80, 0.3);
    transform: translateY(-2px);
}

.bar-mg-btn:active:not(:disabled) {
    transform: scale(0.97);
}

.bar-mg-btn.bar-mg-correct {
    border-color: #e8c860;
    background: rgba(200, 160, 80, 0.15);
    opacity: 0.8;
    pointer-events: none;
    box-shadow: 0 0 12px rgba(232, 200, 96, 0.25), inset 0 0 8px rgba(232, 200, 96, 0.08);
}

.bar-mg-btn.bar-mg-wrong {
    border-color: #944060;
    background: rgba(148, 64, 96, 0.15);
    opacity: 0.35;
    pointer-events: none;
    box-shadow: 0 0 8px rgba(148, 64, 96, 0.2);
}

.bar-mg-btn.bar-mg-done { opacity: 0.3; pointer-events: none; }

.bar-mg-icon { font-size: 18px; line-height: 1; }
.bar-mg-img { width: 38px; height: 38px; object-fit: contain; border-radius: 6px; pointer-events: none; }
.bar-mg-label { font-size: 11px; color: var(--text-dim, #a89ab8); }

/* ── Storage Zones (Crates) ──────────────── */

.bar-mg-zones {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
}

.bar-mg-zone {
    min-height: 46px;
    padding: 5px 4px;
    background: rgba(26, 20, 40, 0.8);
    border: 2px dashed rgba(200, 160, 80, 0.2);
    border-radius: 8px;
    color: var(--text-dim, #a89ab8);
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    transition: all 0.2s ease;
}

.bar-mg-zone:hover {
    border-color: rgba(200, 160, 80, 0.5);
    background: rgba(200, 160, 80, 0.08);
}

.bar-mg-zone-icon { font-size: 16px; }
.bar-mg-zone-label { font-size: 11px; font-weight: 600; color: var(--text, #ddd4ec); }
.bar-mg-zone-count { font-size: 10px; color: #e8c860; }

/* ── Crate Queue ─────────────────────────── */

.bar-mg-crate-queue {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
}

.bar-mg-crate {
    min-height: 44px;
    padding: 5px 4px;
    background: linear-gradient(145deg, rgba(40, 32, 24, 0.9), rgba(30, 24, 18, 0.95));
    border: 1px solid rgba(120, 96, 60, 0.4);
    border-radius: 8px;
    color: var(--text, #ddd4ec);
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    transition: all 0.2s ease;
}

.bar-mg-crate:hover:not(:disabled) {
    border-color: rgba(200, 160, 80, 0.5);
    transform: translateY(-2px);
}

.bar-mg-crate-active {
    border-color: #e8c860 !important;
    box-shadow: 0 0 12px rgba(232, 200, 96, 0.2);
    transform: translateY(-3px);
}

.bar-mg-crate-done {
    opacity: 0.25;
    pointer-events: none;
    border-color: rgba(120, 96, 60, 0.2);
}

.bar-mg-crate-wrong {
    border-color: #944060 !important;
}

.bar-mg-crate-img { width: 40px; height: 40px; object-fit: contain; border-radius: 4px; pointer-events: none; }
.bar-mg-crate-label { font-size: 10px; color: var(--text-dim, #a89ab8); text-align: center; font-weight: 500; }

/* ── Stars ───────────────────────────────── */

.bar-mg-stars {
    display: flex;
    gap: 4px;
    justify-content: center;
    font-size: 18px;
    padding-top: 2px;
    border-top: 1px solid rgba(200, 160, 80, 0.1);
}

.bar-mg-star { transition: color 0.3s ease; }
.bar-mg-star-filled { color: #e8c860; text-shadow: 0 0 8px rgba(232, 200, 96, 0.4); }
.bar-mg-star-empty  { color: rgba(100, 88, 120, 0.4); }

/* ── Skip Button ─────────────────────────── */

.bar-mg-skip {
    align-self: flex-end;
    padding: 6px 16px;
    background: rgba(40, 32, 60, 0.6);
    border: 1px solid rgba(100, 88, 120, 0.3);
    border-radius: 6px;
    color: var(--text-dim, #a89ab8);
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s;
}

.bar-mg-skip:hover { opacity: 1; }

/* ── Shake animation ─────────────────────── */

@keyframes bar-mg-shake {
    0%, 100% { transform: translateX(0); }
    20%      { transform: translateX(-4px); }
    40%      { transform: translateX(4px); }
    60%      { transform: translateX(-3px); }
    80%      { transform: translateX(3px); }
}

.bar-mg-shake { animation: bar-mg-shake 0.4s ease; }

/* ── Mobile ──────────────────────────────── */

@media (max-width: 768px) {
    .bar-mg-grid, .bar-mg-crate-queue {
        grid-template-columns: repeat(2, 1fr);
    }
    .bar-mg-zones {
        grid-template-columns: repeat(2, 1fr);
    }
    .bar-mg-panel {
        padding: 14px;
        gap: 10px;
    }
    .bar-mg-btn, .bar-mg-crate {
        min-height: 52px;
    }
}
`;
        document.head.appendChild(style);
    }
}

// ── Singleton export ───────────────────────────────────────────────────
export const barMinigame = new BarMinigame();
